import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, SpinnerGap } from "@phosphor-icons/react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      } catch (err) {
        console.warn("Payment status check failed, retrying", err?.message || err);
      }
      if (++tries > 10) return setState("timeout");
      setTimeout(tick, 2000);
    };
    tick();
  }, [sessionId]);

  const copy = {
    checking: { icon: SpinnerGap, tone: "text-[#3B82F6] animate-spin", title: "Confirming your payment…", sub: "This only takes a moment." },
    paid: { icon: CheckCircle, tone: "text-[#10B981]", title: "Payment received!", sub: "Your invoice is marked paid. A receipt is on the way." },
    timeout: { icon: SpinnerGap, tone: "text-[#FBBF24]", title: "Still processing", sub: "Your payment is taking longer than usual. Check your invoices in a moment." },
    error: { icon: XCircle, tone: "text-[#EF4444]", title: "We couldn't confirm the payment", sub: "Please try again or call (501) 904-7103." },
  }[state];

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <div className="text-center max-w-lg tpl-fade-up" data-testid={`payment-${state}`}>
        <copy.icon size={72} weight="fill" className={`mx-auto ${copy.tone}`} />
        <h1 className="mt-8 text-4xl font-bold tracking-tight">{copy.title}</h1>
        <p className="mt-4 text-muted-foreground">{copy.sub}</p>
        <Link to="/portal/billing">
          <Button className="mt-9 h-12 px-8 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="back-to-billing-btn">Back to my invoices</Button>
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <div className="text-center max-w-lg" data-testid="payment-cancelled">
        <XCircle size={72} weight="fill" className="mx-auto text-[#FBBF24]" />
        <h1 className="mt-8 text-4xl font-bold tracking-tight">Payment cancelled</h1>
        <p className="mt-4 text-muted-foreground">No charge was made. You can pay any time from your portal.</p>
        <Link to="/portal/billing">
          <Button className="mt-9 h-12 px-8 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="cancel-back-btn">Back to my invoices</Button>
        </Link>
      </div>
    </div>
  );
}
